import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import {
  Voting,
  VotingFile,
  VotingOption,
  VotingState,
} from "src/model/class/general classes/voting";
import { UsersRole } from "src/model/enum/enumDigitalPlace";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { MediaService } from "src/service/media.service";
import { VotingService } from "src/service/voting.service";

/**
 * Vote / results modal.
 *
 * For a resident on an IN_PROGRESS voting it renders the options as radio
 * (single choice) or checkboxes (multiple choice), prefilled with the current
 * vote so it can be changed, and saves via castVote. When results are visible
 * (the voting is CLOSED, or the viewer is the administrator) it shows the
 * per-option percentages instead, plus — for the administrator — who voted what
 * (votes are not anonymous). Attachments can always be downloaded.
 */
@Component({
    selector: "app-popup-voting-vote",
    templateUrl: "./popup-voting-vote.component.html",
    styleUrls: ["./popup-voting-vote.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupVotingVoteComponent implements OnInit, OnDestroy {
  @Input() votingId: number;

  public voting: Voting = null;
  public isLoading: boolean = false;
  public isBuildingAdministrator: boolean = false;

  public votingState = VotingState;

  // Resident selection state.
  public singleSelection: number = null;
  public multipleSelection: { [optionId: number]: boolean } = {};

  // Backend tallies with the CURRENT user's existing vote removed, so the live
  // (WhatsApp-style) preview can re-add the user's current selection on top and
  // recompute percentages instantly as they tap an option.
  private _baseVotes: { [optionId: number]: number } = {};
  private _baseTotalVoters: number = 0;

  public closeLabel: string = "";
  public voteLabel: string = "";
  public resultsLabel: string = "";
  public totalVotersLabel: string = "";
  public attachmentsLabel: string = "";
  public notStartedLabel: string = "";

  private _translationSubscription: Subscription = null;
  private _currentUserSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _votingService: VotingService,
    private _alertService: AlertService,
    private _loaderService: LoaderService,
    private _mediaService: MediaService,
    private _platform: Platform,
    private _translateService: TranslateService,
    private _store: Store,
  ) {}

  async ngOnInit(): Promise<void> {
    this.loadCurrentUserData();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    await this.loadVoting();
  }

  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
    this._currentUserSubscription?.unsubscribe();
  }

  /**
   * Whether the current viewer can cast/change a vote: a non-admin user while
   * the voting is open.
   */
  public get canVote(): boolean {
    return (
      !this.isBuildingAdministrator &&
      this.voting?.state === VotingState.IN_PROGRESS
    );
  }

  /**
   * Whether per-option results (percentages) should be displayed.
   */
  public get showResults(): boolean {
    return !!this.voting?.resultsVisible;
  }

  private loadCurrentUserData(): void {
    this._currentUserSubscription = this._store
      .select(currentUserData)
      .pipe(
        filter((data: GenericUserData) => data !== null),
        map((data: GenericUserData) => data?.currentUser),
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this.isBuildingAdministrator =
          currentUser?.role === UsersRole.BUILDING_ADMINISTRATOR;
      });
  }

  /**
   * Load the fresh voting (with results gated by role/state) and seed the
   * selection from the user's existing vote.
   */
  private async loadVoting(): Promise<void> {
    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();
      const data = await this._votingService.getResults(this.votingId);
      this.voting = new Voting(data);
      this.seedSelection();
      this.computeBaseTallies();
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Prefill the selection controls from the user's current vote.
   */
  private seedSelection(): void {
    const userOptionIds = this.voting?.userOptionIds || [];
    if (this.voting?.multipleChoice) {
      this.multipleSelection = {};
      userOptionIds.forEach((id) => (this.multipleSelection[id] = true));
    } else {
      this.singleSelection = userOptionIds.length ? userOptionIds[0] : null;
    }
  }

  /**
   * Toggle a checkbox option (multiple-choice voting).
   */
  public onToggleOption(optionId: number, checked: boolean): void {
    this.multipleSelection[optionId] = checked;
  }

  /**
   * Whether the given option is part of the current (unsaved) selection.
   */
  public isOptionSelected(option: VotingOption): boolean {
    return this.voting?.multipleChoice
      ? !!this.multipleSelection[option.id]
      : this.singleSelection === option.id;
  }

  /**
   * Select an option from the unified poll list (WhatsApp-style). Single choice
   * replaces the selection; multiple choice toggles it. The live bars recompute
   * automatically from the new selection.
   */
  public selectOption(option: VotingOption): void {
    if (!this.canVote) {
      return;
    }
    if (this.voting?.multipleChoice) {
      this.multipleSelection[option.id] = !this.multipleSelection[option.id];
    } else {
      this.singleSelection = option.id;
    }
  }

  /**
   * Whether the user currently has any option selected (enables the Vote CTA).
   */
  public get hasSelection(): boolean {
    return this.collectSelectedOptionIds().length > 0;
  }

  /**
   * Snapshot the backend tallies without the current user's existing vote, so
   * the live preview can re-add their current selection on top.
   */
  private computeBaseTallies(): void {
    const userOptionIds = this.voting?.userOptionIds || [];
    const userHadVoted = userOptionIds.length > 0;

    this._baseTotalVoters =
      (this.voting?.totalVoters || 0) - (userHadVoted ? 1 : 0);

    this._baseVotes = {};
    (this.voting?.options || []).forEach((option) => {
      const alreadyOnThis = userOptionIds.includes(option.id) ? 1 : 0;
      this._baseVotes[option.id] = (option.votes || 0) - alreadyOnThis;
    });
  }

  /**
   * Live vote count for an option: while the user can vote it reflects their
   * current (unsaved) selection on top of the base tally (WhatsApp-style);
   * otherwise it shows the backend value as-is.
   */
  public displayVotes(option: VotingOption): number {
    if (!this.canVote) {
      return option.votes || 0;
    }
    const selected = this.collectSelectedOptionIds();
    const base = this._baseVotes[option.id] || 0;
    return base + (selected.includes(option.id) ? 1 : 0);
  }

  /**
   * Live total voters: the base plus one if the user has any option selected.
   */
  public get displayTotalVoters(): number {
    if (!this.canVote) {
      return this.voting?.totalVoters || 0;
    }
    const selected = this.collectSelectedOptionIds();
    return this._baseTotalVoters + (selected.length > 0 ? 1 : 0);
  }

  /**
   * Participation rate: how many of the eligible voters have cast a vote,
   * as a percentage (voters / eligible). Guards against a zero/missing
   * denominator. Used next to the "total voters" X/Y figure.
   */
  public participationPercentage(voters: number): number {
    const eligible = this.voting?.totalEligible || 0;
    if (eligible <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((voters / eligible) * 100));
  }

  /**
   * Live percentage for an option over the live total voters.
   */
  public displayPercentage(option: VotingOption): number {
    const total = this.displayTotalVoters;
    if (total <= 0) {
      return 0;
    }
    return Math.round((this.displayVotes(option) / total) * 100);
  }

  /**
   * Highlight in the results the option(s) with the highest percentage (none when nobody voted)
   */
  public isLeadingOption(option: VotingOption): boolean {
    const max = Math.max(
      ...(this.voting?.options || []).map((current) => current.percentage || 0),
    );
    return max > 0 && (option.percentage || 0) === max;
  }

  public onCancel(): void {
    this._modalController.dismiss({ saved: false });
  }

  /**
   * Persist the current selection as the user's vote, then close signalling a
   * change so the list can refresh.
   */
  public async onClickVote(): Promise<void> {
    const optionIds = this.collectSelectedOptionIds();

    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();
      await this._votingService.castVote(this.votingId, optionIds);
      this._modalController.dismiss({ saved: true });
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Download an attachment: fetch the serialized buffer, rebuild the blob and
   * save it via the platform-specific handler.
   */
  public async onClickDownloadFile(file: VotingFile): Promise<void> {
    if (!file?.id) return;

    try {
      await this._loaderService.presentLoaderWaiting();
      const responseData = await this._votingService.getUploadedFile(file.id);
      const blob: Blob = this._mediaService.jsonStringToBlob(responseData.data);

      if (this._platform.is("ios") || this._platform.is("android")) {
        await this._mediaService.handleMobileDownload(blob, file.filename);
      } else {
        this._mediaService.handleDesktopBrowserDownload(file.filename, blob);
      }
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }

  /**
   * Collect the currently selected option ids from the proper control.
   */
  private collectSelectedOptionIds(): Array<number> {
    if (this.voting?.multipleChoice) {
      return Object.keys(this.multipleSelection)
        .filter((id) => this.multipleSelection[+id])
        .map((id) => +id);
    }
    return this.singleSelection != null ? [this.singleSelection] : [];
  }

  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.close",
        "dictionary.votingVote",
        "dictionary.votingResults",
        "dictionary.votingTotalVoters",
        "dictionary.votingAttachments",
        "dictionary.votingNotStarted",
      ])
      .subscribe((translation) => {
        this.closeLabel = translation["dictionary.close"];
        this.voteLabel = translation["dictionary.votingVote"];
        this.resultsLabel = translation["dictionary.votingResults"];
        this.totalVotersLabel = translation["dictionary.votingTotalVoters"];
        this.attachmentsLabel = translation["dictionary.votingAttachments"];
        this.notStartedLabel = translation["dictionary.votingNotStarted"];
      });
  }

  private subscribeTranslationEvent(): void {
    this._translationSubscription =
      this._translateService.onLangChange.subscribe(() => {
        this.loadTranslation();
      });
  }
}
